import { createContext, useCallback, useContext, useEffect, useReducer, useState } from 'react';
import useDebounceFunction from '../../../../../components/Hooks/useDebouncedFunction';
import { fetcher } from '../../../../../components/Hooks/useListActions';
import useMultipleFileUploads, {
  UploadMultipleFilesOutputStatusEnum,
} from '../../../../../hooks/useMultipleFileUploads';
import LangEnum from '../../../../../lang/LangEnum';
import snackbarStore from '../../../../../stores/Snackbar.store';
import { useTranslation } from '../../../../translation/TranslationContext';
import { useChatContext } from '../../../contexts/chat/chat.context';
import { ChatState } from '../../../contexts/chat/hooks/useChats/reducer/chatListReducer.types';
import { useChatSocketContext } from '../../../contexts/chatSocket/hooks/useChatSocketContext';
import { Message } from '../../../contexts/chatSocket/reducer/chatSocketReducer.types';
import { isVideoCallMessageType } from '../../../contexts/chatSocket/reducer/chatSocketReducer.utils';
import { MessageType } from '../../../types/chat.types';
import { groupUploadedFilesByMessageType } from '../components/ChatMessageView/components/ChatMessageContent/components/ChatMessageForm/chatMessageForm.utils';
import {
  ChatMessagesContextProps,
  ChatMessagesContextProviderProps,
  FetchMessagesHistoryByChat,
  SubmitMessageParams,
} from './chatMessages.context.types';
import { MAX_FILES_SIZE, validateFileAndGetErrorMessage } from './chatMessages.context.utilts';
import { chatMessagesReducer, getChatMessagesInitialState } from './reducer/chatMessages.state';
import {
  fetchMessagesResult,
  fetchingMore,
  goToChat,
  goToEditChat,
  incrementNewWsMessage,
  removeWsMessage,
  setMessagesOfType,
} from './reducer/chatMessages.state.actions';
import {
  ChatMessageAcceptedMessageTypesToFilter,
  ChatMessageViewEnum,
} from './reducer/chatMessages.state.types';

const DEFAULT_ITEMS_PER_PAGE = 30;

const ChatContext = createContext<ChatMessagesContextProps>({
  messages: [],
  isFirstLoading: false,
  totalItems: 0,
  firstItemIndex: 0,
  isLoading: false,
  footerMessage: '',
  headerMessage: '',
  currentChat: {} as ChatState,
  isSendingFirstMessageInChatWithoutId: false,
  currentView: ChatMessageViewEnum.CHAT_MESSAGES,
  goToChat: () => undefined,
  goToEditChat: () => undefined,
  onClickRemove: () => undefined,
  initVideoCall: () => undefined,
  filterMessagesBy: () => undefined,
  fetchMoreMessages: () => undefined,
  submitFiles: async () => undefined,
  submitMessage: async () => undefined,
});

export const useChatMessagesContext = () => useContext<ChatMessagesContextProps>(ChatContext);

export const ChatMessagesContextProvider = ({
  chat,
  children,
}: ChatMessagesContextProviderProps) => {
  const { translate } = useTranslation();
  const { uploadMultipleFiles } = useMultipleFileUploads();

  const {
    updateLastViewedMessageInChat,
    getCountOfChatsWithNotificationsByChatIds,
    onSubmitMessage,
    onClickStartVideoCall,
    onClickJoinVideoCall,
    videoCall,
    addVideoCallInActiveChat,
  } = useChatContext();

  const {
    websocketMessages,
    cleanMessagesByTempId,
    cleanMessagesByMessageId,
    errorRecoveringStateAfterDisconnect,
  } = useChatSocketContext();

  const [state, dispatch] = useReducer(chatMessagesReducer, getChatMessagesInitialState());

  const [isSendingFirstMessageInChatWithoutId, setIsSendingFirstMessageInChatWithoutId] =
    useState(false);

  const handleGoToEditChat = useCallback(() => {
    dispatch(goToEditChat());
  }, []);

  const handleGoToChat = useCallback(() => {
    dispatch(goToChat());
  }, []);

  const filterMessagesBy = useCallback((filterBy?: ChatMessageAcceptedMessageTypesToFilter) => {
    dispatch(setMessagesOfType({ filterBy }));
  }, []);

  const handleNotificationsInFirstPageLoad = (loadedMessages: Message[]) => {
    const lastMessage = loadedMessages.find((message) => message.id);

    if (!chat.id || !lastMessage?.id) {
      return;
    }

    const notificationsToChat = getCountOfChatsWithNotificationsByChatIds([chat.id]);

    if (!notificationsToChat) {
      return;
    }

    updateLastViewedMessageInChat(chat.id, lastMessage.id);
  };

  const fetchMessagesHistoryByChat = useCallback(
    async ({ chatId, page }: FetchMessagesHistoryByChat) => {
      dispatch(fetchingMore());
      const response = await fetcher<Message>(`/chats/${chatId}/messages`)({
        count: DEFAULT_ITEMS_PER_PAGE,
        page,
        params: {
          type: state.filterMessagesOfType,
        },
      });

      if (response.tag === 'success') {
        const messages = response.data;
        if (page === 0) {
          handleNotificationsInFirstPageLoad(messages);
        }
      }

      dispatch(fetchMessagesResult(response));
    },
    [state.filterMessagesOfType],
  );

  const concatNewWsMessage = useCallback((message: Message) => {
    dispatch(incrementNewWsMessage({ message }));
  }, []);

  const handleClickRemoveMessage = useCallback((messageTemporaryId: string) => {
    dispatch(removeWsMessage({ messageTemporaryId }));
    cleanMessagesByTempId(messageTemporaryId);
  }, []);

  const fetchMoreMessages = () => {
    if (state.isLoading || !state.hasMore || !chat.id) {
      return;
    }

    fetchMessagesHistoryByChat({ chatId: chat.id, page: state.page });
  };

  const updateChatSocketMessages = (messageReceived: Message) => {
    if (messageReceived.tempId && messageReceived.id) {
      cleanMessagesByTempId(messageReceived.tempId);
    }
    if (messageReceived.id) {
      cleanMessagesByMessageId(messageReceived.id);
    }
  };

  const handleIsSendingFirstMessageInChatWithoutId = async (callback: () => Promise<void>) => {
    if (chat.id) {
      return callback();
    }

    setIsSendingFirstMessageInChatWithoutId(true);
    try {
      await callback();
    } finally {
      setIsSendingFirstMessageInChatWithoutId(false);
    }
  };

  const validateFiles = (files: File[]) => {
    if (files.length > MAX_FILES_SIZE) {
      snackbarStore.handleMessage({
        type: 'error',
        message: translate(LangEnum.CHAT_MAX_FILES, { maxFilesSize: MAX_FILES_SIZE }),
      });
      return false;
    }

    const errors = files
      .map((file) => validateFileAndGetErrorMessage(file, translate))
      .filter((file) => !!file);

    if (errors.length) {
      snackbarStore.handleMessage({
        type: 'error',
        message: errors.join('\n'),
      });
      return false;
    }

    return true;
  };

  const submitFiles = async (files: File[]) => {
    const isValidFiles = validateFiles(files);

    if (!isValidFiles) {
      return;
    }

    const filesData = await uploadMultipleFiles(files);

    const successUploadedFiles = filesData.filter(
      (fileData) => fileData.status === UploadMultipleFilesOutputStatusEnum.SUCCESS,
    );
    const errorUploadedFiles = filesData.filter(
      (fileData) => fileData.status === UploadMultipleFilesOutputStatusEnum.ERROR,
    );

    if (errorUploadedFiles.length) {
      setTimeout(() => {
        snackbarStore.handleMessage({
          type: 'info',
          message: translate(LangEnum.CHAT_ERROR_IN_SOME_FILES_OR_IMAGES),
        });
      }, 1000);
    }

    const groupedByMessageTypes = groupUploadedFilesByMessageType(successUploadedFiles);

    const numberOfUploadedFiles = groupedByMessageTypes.FILE.length;
    if (numberOfUploadedFiles) {
      onSubmitMessage({
        type: MessageType.FILE,
        data: { urlAndNames: groupedByMessageTypes.FILE },
        text: translate(LangEnum.CHAT_UPLOADED_FILES_SIZE, {
          numberOfFiles: numberOfUploadedFiles,
        }),
      });
    }

    const numberOfUploadedImages = groupedByMessageTypes.IMAGE_OR_VIDEO.length;
    if (numberOfUploadedImages) {
      onSubmitMessage({
        type: MessageType.IMAGE_OR_VIDEO,
        data: { urlNameAndTypes: groupedByMessageTypes.IMAGE_OR_VIDEO },
        text: translate(LangEnum.CHAT_UPLOADED_IMAGES_SIZE, {
          numberOfImages: numberOfUploadedImages,
        }),
      });
    }
  };

  const submitMessage = async (values: SubmitMessageParams) => {
    await onSubmitMessage({
      data: undefined,
      type: MessageType.MESSAGE,
      text: values.messageText.trim(),
    });
  };

  const initVideoCall = useCallback(async () => {
    if (videoCall) {
      await onClickJoinVideoCall();
    } else {
      await onClickStartVideoCall();
    }
  }, [onClickJoinVideoCall, onClickStartVideoCall, videoCall]);

  const { handleDebounceFunction: handleDebounceUpdateLastViewedMessage } = useDebounceFunction({
    debounceTime: 2000,
  });
  const updateLastViewedMessageOfCurrentChat = (lastMessageId: string) => {
    if (!chat.id) {
      return;
    }
    updateLastViewedMessageInChat(chat.id, lastMessageId);
  };

  const { handleDebounceFunction: debounceConcatWsMessageWithChat } = useDebounceFunction({
    debounceTime: 60,
  });
  const concatWsMessagesWithChat = () => {
    const newWsMessagesReceived = websocketMessages.filter(
      (wsMessage) => wsMessage.chatId === chat.id,
    );

    if (!newWsMessagesReceived.length) {
      return;
    }

    const lastWsMessageWithId = newWsMessagesReceived.findLast((message) => message.id);
    const lastWsMessageId = lastWsMessageWithId?.id;

    if (lastWsMessageId) {
      handleDebounceUpdateLastViewedMessage(() =>
        updateLastViewedMessageOfCurrentChat(lastWsMessageId),
      );
    }

    newWsMessagesReceived.forEach((newWsMessageReceived) => {
      concatNewWsMessage(newWsMessageReceived);
      updateChatSocketMessages(newWsMessageReceived);

      if (isVideoCallMessageType(newWsMessageReceived)) {
        addVideoCallInActiveChat(newWsMessageReceived.data);
      }
    });
  };

  useEffect(() => {
    debounceConcatWsMessageWithChat(concatWsMessagesWithChat);
  }, [chat.id, websocketMessages]);

  useEffect(() => {
    if (!chat.id) {
      return;
    }

    fetchMessagesHistoryByChat({ chatId: chat.id, page: 0 });
  }, [chat.id, fetchMessagesHistoryByChat]);

  useEffect(() => {
    if (!errorRecoveringStateAfterDisconnect.hasErrorRecoveringState || !chat.id) {
      return;
    }

    fetchMessagesHistoryByChat({ chatId: chat.id, page: 0 });
  }, [errorRecoveringStateAfterDisconnect]);

  const values: ChatMessagesContextProps = {
    currentChat: chat,
    filterMessagesBy,
    currentView: state.view,
    messages: state.messages,
    isLoading: state.isLoading,
    totalItems: state.totalItems,
    firstItemIndex: state.firstItemIndex,
    isSendingFirstMessageInChatWithoutId,
    filteringMessagesByType: state.filterMessagesOfType,
    isFirstLoading: state.isLoading && state.page === 0,
    submitFiles: (params) => handleIsSendingFirstMessageInChatWithoutId(() => submitFiles(params)),
    submitMessage: (params) =>
      handleIsSendingFirstMessageInChatWithoutId(() => submitMessage(params)),
    initVideoCall: () => handleIsSendingFirstMessageInChatWithoutId(initVideoCall),
    fetchMoreMessages,
    goToChat: handleGoToChat,
    goToEditChat: handleGoToEditChat,
    onClickRemove: handleClickRemoveMessage,
    footerMessage:
      chat.wasRemoved && state.messages.length
        ? translate(LangEnum.CHAT_ROOM_MESSAGE_USER_REMOVED)
        : '',
    headerMessage:
      chat?.wasRemoved && !state.messages.length
        ? translate(LangEnum.CHAT_ROOM_MESSAGE_USER_REMOVED)
        : translate(LangEnum.CHAT_ROOM_MESSAGE_INIT),
  };

  return <ChatContext.Provider value={values}>{children}</ChatContext.Provider>;
};
